import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { setupInterceptors } from "../axios/axiosConfig";
import usersService from "../../services/usersService";
import { AppSplash } from "../../components";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { signOut } = useClerk();

  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    setupInterceptors({
      getToken: async (opts) => await getToken(opts),
      logout: async () => await signOut(),
    });
  }, [isLoaded, getToken, signOut]);

  const fetchCurrentUser = useCallback(async () => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      if (!mountedRef.current) return;
      setCurrentUser(null);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }

      const res = await usersService.getCurrentUser();

      if (!mountedRef.current) return;

      const user =
        res?.data?.data?.user ??
        res?.data?.user ??
        res?.data?.data ??
        null;

      setCurrentUser(user);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;

      console.error("Error fetching current user:", err);

      setCurrentUser(null);

      const status = err?.response?.status;

      if (status === 401) {
        setError("Authentication required");
      } else if (status === 403) {
        setError("Your account is not allowed to access this application");
      } else if (status === 404) {
        setError("User profile not found");
      } else {
        setError("Failed to load user data");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const value = {
    currentUser,
    setCurrentUser,
    loading,
    error,
    refetch: fetchCurrentUser,
    isAuthenticated: !!currentUser,
    userRole: currentUser?.role?.name || null,
    userPermissions: Array.isArray(currentUser?.role?.permissions)
      ? currentUser.role.permissions
      : [],
  };

  const showSplash = !isLoaded || (isSignedIn && loading);

  return (
    <UserContext.Provider value={value}>
      {showSplash ? <AppSplash text="Loading your account..." /> : children}
    </UserContext.Provider>
  );
};

export const useCurrentUser = () => {
  const ctx = useContext(UserContext);

  if (!ctx) {
    throw new Error("useCurrentUser must be used inside <UserProvider />");
  }

  return ctx;
};